import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, X, EyeOff, ExternalLink } from "lucide-react";
import { adminApi, AdminHeader, EmptyState } from "@/lib/adminApi";
import { CuratorBar } from "@/components/admin/CuratorBar";

const ACTIONS = {
  pending: [["approve", "Approve", Check, "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"], ["reject", "Reject", X, "bg-red-500/10 text-red-300 hover:bg-red-500/20"]],
  approved: [["hide", "Hide", EyeOff, "bg-white/5 text-[var(--text-secondary)] hover:text-white"]],
};

const PulseRow = ({ item, onAction }) => (
  <div className="bg-[var(--bg-card)] border-glow rounded-xl p-5 flex items-start justify-between gap-4" data-testid={`pulse-item-${item._id}`}>
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-wider">
        <span className="px-2 py-0.5 rounded bg-[var(--accent-violet)]/15 text-[var(--accent-violet-light)] font-semibold">{item.type}</span>
        {item.company && <span className="text-[var(--text-secondary)]">{item.company}</span>}
      </div>
      <p className="text-white font-semibold">{item.headline}</p>
      <p className="text-sm text-[var(--text-secondary)] mt-1">{item.why}</p>
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[var(--accent-violet-light)] mt-2"><ExternalLink className="w-3 h-3" /> {item.source}</a>
    </div>
    <div className="flex gap-2 flex-shrink-0">
      {ACTIONS[item.status].map(([act, lbl, Icon, cls]) => (
        <button key={act} onClick={() => onAction(item._id, act)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${cls}`} data-testid={`pulse-${act}-${item._id}`}><Icon className="w-3.5 h-3.5" /> {lbl}</button>
      ))}
    </div>
  </div>
);

const AdminPulse = () => {
  const [items, setItems] = useState([]);
  const load = () => adminApi("/admin/pulse").then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);

  const onAction = async (id, act) => {
    try { await adminApi(`/admin/pulse/${id}/${act}`, { method: "POST" }); toast.success(`Brief ${act === "approve" ? "approved — now live on /pulse" : act === "hide" ? "hidden" : "rejected"}`); load(); }
    catch (e) { toast.error(e.message); }
  };

  const pending = items.filter((i) => i.status === "pending");
  const live = items.filter((i) => i.status === "approved");
  return (
    <div className="max-w-5xl">
      <AdminHeader title="AI Pulse" subtitle="Short AI-industry news briefs. Approve to publish them on the public /pulse page." testId="admin-pulse-heading" />
      <CuratorBar onFinished={load} />
      <h2 className="text-sm uppercase tracking-wider text-[var(--text-secondary)] mb-3">Awaiting review ({pending.length})</h2>
      <div className="space-y-3 mb-10">
        {pending.length ? pending.map((i) => <PulseRow key={i._id} item={i} onAction={onAction} />) : <EmptyState text="No briefs waiting for review" testId="pulse-pending-empty" />}
      </div>
      <h2 className="text-sm uppercase tracking-wider text-[var(--text-secondary)] mb-3">Live on /pulse ({live.length})</h2>
      <div className="space-y-3">
        {live.length ? live.map((i) => <PulseRow key={i._id} item={i} onAction={onAction} />) : <EmptyState text="Nothing published yet" testId="pulse-live-empty" />}
      </div>
    </div>
  );
};

export default AdminPulse;
